<template>
  <div class="list-container">
    <div class="top-fix-area" ref="topFixArea">
      <h1 class="page-title">建物情報一覧</h1>
      <div class="mb-2">
        <router-link v-if="isAdmin" :to="{ path: '/create-building', query: getSearchQuery() }" class="btn btn-sm btn-primary me-2">
          建物情報作成
        </router-link>

        <button v-if="isAdmin" type="button" class="btn btn-sm btn-primary me-2" data-bs-toggle="modal" data-bs-target="#exampleModal">
          CSVインポート
        </button>

        <button type="button" class="btn btn-sm btn-primary me-2" @click="downloadCsv">
          CSVエクスポート
        </button>

        <button class="btn btn-sm btn-outline-secondary" @click="isSearchExpanded = !isSearchExpanded">
          {{ isSearchExpanded ? '検索条件を非表示' : '検索条件を表示' }}
        </button>
      </div>

      <div class="modal fade" id="exampleModal" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
            <div class="modal-header">
              <h1 class="modal-title fs-5">CSVインポート</h1>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" @click="clearErrorMessages"></button>
            </div>
            <div class="modal-body">
              <div v-if="errorMessages">
                <div class="error-massage-item" v-for="errorMessage in errorMessages" :key="errorMessage.id">
                  <p class="alert alert-danger mt-2">{{ errorMessage.row_num }}行目：{{ errorMessage.messages[0] }}</p>
                </div>
              </div>

              <div class="my-3">
                <p>建物情報CSVファイル選択</p>
                <input id="buildingCsvFileInput" type="file" class="form-control" @change="handleFileChange">
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="clearErrorMessages">閉じる</button>
              <button type="button" class="btn btn-primary" @click="submitFile">インポート</button>
            </div>
          </div>
        </div>
      </div>

      <SearchPanelWrapper :key="route.fullPath" v-model="isSearchExpanded" :getTableElement="() => tableWrapper?.$el || tableWrapper?.value">
        <form class="search-container" @submit.prevent="searchBuildings">
          <div class="row g-3 align-items-center">
            <div class="col-auto">
              <label for="searchCustomerName" class="col-form-label">顧客名</label>
            </div>
            <div class="col-auto">
              <select id="searchCustomerName" class="form-select" v-model="searchCustomerName">
                <option value="">すべて</option>
                <option v-for="customer in customers" :key="customer.id" :value="customer.id">{{ customer.customer_nm }}</option>
              </select>
            </div>

            <div class="col-auto">
              <label for="searchBuildingTypeName" class="col-form-label">建物種別</label>
            </div>
            <div class="col-auto">
              <select id="searchBuildingTypeName" class="form-select" v-model="searchBuildingTypeName">
                <option value="">すべて</option>
                <option v-for="buildingType in buildingTypes" :key="buildingType.id" :value="buildingType.id">{{ buildingType.building_type_nm }}</option>
              </select>
            </div>

            <div class="col-auto">
              <label for="searchBuildingName" class="col-form-label">建物名</label>
            </div>
            <div class="col-auto">
              <input id="searchBuildingName" v-model="searchBuildingName" type="text" class="form-control" placeholder="建物名で検索">
            </div>

            <DeletedStatusSelect v-model="searchDeletedStatus" id="buildingDeletedStatus" />

            <div class="col-auto">
              <button type="submit" class="btn btn-sm btn-primary mx-1">検索</button>
              <button type="button" class="btn btn-sm btn-secondary mx-1" @click="searchResetBuildings">クリア</button>
            </div>
          </div>
        </form>
      </SearchPanelWrapper>
    </div>

    <div class="d-flex justify-content-end" ref="paginationContainer">
      <Pagination
        :currentPage="currentPage"
        :totalPages="totalPages"
        @change-page="handleChangePage"
      />
    </div>

    <div class="table-wrapper" ref="tableWrapper">
      <table class="master-table table table-bordered">
        <thead class="table-dark">
          <tr>
            <th class="text-nowrap">ID</th>
            <th class="text-nowrap">建物名</th>
            <th class="text-nowrap">区分</th>
            <th class="text-nowrap">顧客名</th>
            <th class="text-nowrap">所在地</th>
            <th class="text-nowrap">電話番号</th>
            <th class="text-nowrap">担当者</th>
            <th class="btn-cell"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="building in buildings" :key="building.id" :class="{ 'no-active-building': building.del_flg == 1 }">
            <td>{{ building.id }}</td>
            <td>{{ building.building_nm }}</td>
            <td class="text-nowrap">{{ building.m_building_type?.building_type_nm }}</td>
            <td>{{ building.m_customer?.customer_nm }}</td>
            <td>〒{{ building.postal_code }} {{ building.prefecture }}{{ building.city }}{{ building.other_address }}</td>
            <td class="text-nowrap">{{ building.tel_num }}</td>
            <td class="text-nowrap">{{ building.in_charge_nm }}</td>
            <td class="btn-cell text-nowrap">
              <router-link :to="{ name: 'BuildingDetail', params: { id: building.id }, query: getSearchQuery() }">
                <button type="button" class="btn btn-sm btn-info">詳細</button>
              </router-link>

              <router-link v-if="isAdmin" :to="{ name: 'BuildingEdit', params: { id: building.id }, query: getSearchQuery() }">
                <button type="button" class="btn btn-sm btn-success mx-2">編集</button>
              </router-link>

              <button
                v-if="building.del_flg == 0 && isAdmin"
                class="btn btn-sm btn-danger"
                data-bs-toggle="modal"
                data-bs-target="#deleteModal"
                @click="handleDeleteClick(building.id)"
              >
                削除
              </button>
              <button
                v-if="building.del_flg == 1 && isAdmin"
                class="btn btn-sm btn-warning"
                data-bs-toggle="modal"
                data-bs-target="#restoreModal"
                @click="handleRestoreClick(building.id)"
              >
                復元
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <DeleteConfirmationModal
      :paramsId="paramsIdToDelete"
      @confirmDelete="handleConfirmDelete"
    />

    <RestoreConfirmationModal
      :paramsId="paramsIdToRestore"
      @confirmRestore="handleConfirmRestore"
    />
  </div>
</template>

<script>
import { ref, onMounted, watchEffect, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import {
  getBuildings,
  getCustomers,
  getBuildingTypes,
  deleteBuilding,
  restoreBuilding,
  exportBuildingsCsv,
  importBuildingsCsv
} from '@/api/axios';
import { Modal } from 'bootstrap';
import SearchPanelWrapper from '@/components/searchPanels/SearchPanelWrapper.vue';
import DeleteConfirmationModal from '@/components/shared/DeleteConfirmationModal.vue';
import RestoreConfirmationModal from '@/components/shared/RestoreConfirmationModal.vue';
import Pagination from '@/components/shared/Pagination.vue';
import DeletedStatusSelect from '@/components/shared/DeletedStatusSelect.vue';
import { currentRoleFromStoreOrLocalStorage, isAdminRole } from '@/utils/roleAuthorization';
import { filterActiveRecords } from '@/utils/filterActiveRecords';

export default {
  components: {
    SearchPanelWrapper,
    DeleteConfirmationModal,
    RestoreConfirmationModal,
    Pagination,
    DeletedStatusSelect
  },
  setup() {
    const title = ref('建物情報一覧');

    watchEffect(() => {
      document.title = title.value;
    });

    const store = useStore();
    const isAdmin = computed(() => isAdminRole(currentRoleFromStoreOrLocalStorage(store.state.userRole)));
    const router = useRouter();
    const route = useRoute();

    const buildings = ref([]);
    const customers = ref([]);
    const buildingTypes = ref([]);

    const selectedFile = ref(null);
    const errorMessages = ref({});

    const paramsIdToDelete = ref(null);
    const paramsIdToRestore = ref(null);

    const searchCustomerName = ref('');
    const searchBuildingTypeName = ref('');
    const searchBuildingName = ref('');
    const searchDeletedStatus = ref('active');

    const currentPage = ref(1);
    const totalPages = ref(1);

    const isSearchExpanded = ref(true);
    const topFixArea = ref(null);
    const tableWrapper = ref(null);
    const paginationContainer = ref(null);

    const getSearchQuery = () => ({
      building_customer_id: searchCustomerName.value,
      building_building_type_id: searchBuildingTypeName.value,
      building_name: searchBuildingName.value,
      building_deleted_status: searchDeletedStatus.value,
      building_page: currentPage.value || 1
    });

    const fetchSearchOptions = async () => {
      try {
        const customersRes = await getCustomers({ page: 1, deleted_status: 'active', include_visibility_hidden: true });
        customers.value = filterActiveRecords(customersRes.data.customers || []);
      } catch (error) {
        console.error('顧客情報データの取得に失敗しました:', error);
      }

      try {
        const buildingTypesRes = await getBuildingTypes({ deleted_status: 'active', include_visibility_hidden: true, page: 1 });
        buildingTypes.value = filterActiveRecords(buildingTypesRes.data.building_types || []);
      } catch (error) {
        console.error('建物種別情報データの取得に失敗しました:', error);
      }
    };

    const fetchBuildings = async (page = 1) => {
      try {
        const response = await getBuildings({
          customer_id: searchCustomerName.value,
          building_type_id: searchBuildingTypeName.value,
          building_nm: searchBuildingName.value,
          deleted_status: searchDeletedStatus.value,
          include_visibility_hidden: true,
          page
        });

        buildings.value = response.data.buildings || [];
        currentPage.value = response.data.meta?.current_page || 1;
        totalPages.value = Math.max(response.data.meta?.total_pages || 1, 1);
      } catch (error) {
        console.error('建物情報データの取得に失敗しました:', error);
      }
    };

    const handleChangePage = async (page) => {
      if (page < 1 || page > totalPages.value) return;

      await fetchBuildings(page);

      const q = getSearchQuery();
      q.building_page = page;
      router.replace({ path: '/buildings', query: q });

      if (tableWrapper.value) {
        tableWrapper.value.scrollTop = 0;
      }
    };

    const handleDeleteClick = (buildingId) => {
      paramsIdToDelete.value = buildingId;
    };

    const handleRestoreClick = (buildingId) => {
      paramsIdToRestore.value = buildingId;
    };

    const handleConfirmDelete = async (buildingId) => {
      try {
        await deleteBuilding(buildingId, { include_visibility_hidden: true });
        store.commit('setFlashMessage', { message: '建物情報が削除されました', type: 'success' });
        await fetchBuildings(currentPage.value);
      } catch (error) {
        store.commit('setFlashMessage', { message: '建物情報削除に失敗しました', type: 'danger' });
      }
    };

    const handleConfirmRestore = async (buildingId) => {
      try {
        await restoreBuilding(buildingId, { include_visibility_hidden: true });
        store.commit('setFlashMessage', { message: '建物情報が復元されました', type: 'success' });
        await fetchBuildings(currentPage.value);
      } catch (error) {
        store.commit('setFlashMessage', { message: '建物情報復元に失敗しました', type: 'danger' });
      }
    };

    const downloadCsv = async () => {
      try {
        const response = await exportBuildingsCsv({
          customer_id: searchCustomerName.value,
          building_type_id: searchBuildingTypeName.value,
          building_nm: searchBuildingName.value,
          deleted_status: searchDeletedStatus.value,
          include_visibility_hidden: true
        });

        const now = new Date();
        const timestamp = `${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}_${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${now.getSeconds().toString().padStart(2, '0')}`;
        const fileName = `m_buildings_${timestamp}.csv`;

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('CSVダウンロードエラー', error);
      }
    };

    const handleFileChange = (event) => {
      selectedFile.value = event.target.files[0];
    };

    const closeModal = () => {
      const modalElement = document.getElementById('exampleModal');
      const modal = Modal.getInstance(modalElement) || new Modal(modalElement);
      modal.hide();
    };

    const clearErrorMessages = () => {
      errorMessages.value = {};
      selectedFile.value = null;

      const input = document.getElementById('buildingCsvFileInput');
      if (input) {
        input.value = '';
      }
    };

    const submitFile = async () => {
      if (!selectedFile.value) {
        store.commit('setFlashMessage', { message: 'ファイルが選択されていません', type: 'danger' });
        return;
      }

      try {
        await importBuildingsCsv(selectedFile.value);
        store.commit('setFlashMessage', { message: 'アップロードに成功しました', type: 'success' });
        closeModal();
        clearErrorMessages();
        await fetchBuildings(currentPage.value);
      } catch (error) {
        if (error.response && error.response.data) {
          errorMessages.value = error.response.data || {};
        }
        console.error('アップロードに失敗しました', error);
        store.commit('setFlashMessage', { message: 'アップロードに失敗しました', type: 'danger' });
      }
    };

    const searchBuildings = async () => {
      await fetchBuildings(1);

      const q = getSearchQuery();
      q.building_page = 1;
      router.push({ path: '/buildings', query: q });
    };

    const searchResetBuildings = async () => {
      searchCustomerName.value = '';
      searchBuildingTypeName.value = '';
      searchBuildingName.value = '';
      searchDeletedStatus.value = 'active';
      await fetchBuildings(1);

      const q = getSearchQuery();
      q.building_page = 1;
      router.replace({ path: '/buildings', query: q });
    };

    onMounted(async () => {
      const q = route.query;
      searchCustomerName.value = q.building_customer_id || '';
      searchBuildingTypeName.value = q.building_building_type_id || '';
      searchBuildingName.value = q.building_name || '';
      searchDeletedStatus.value = q.building_deleted_status || 'active';
      currentPage.value = q.building_page ? Number(q.building_page) : 1;

      await fetchSearchOptions();
      await fetchBuildings(currentPage.value);
    });

    return {
      title,
      isAdmin,
      route,
      buildings,
      customers,
      buildingTypes,
      paramsIdToDelete,
      paramsIdToRestore,
      handleDeleteClick,
      handleRestoreClick,
      handleConfirmDelete,
      handleConfirmRestore,
      downloadCsv,
      selectedFile,
      handleFileChange,
      submitFile,
      errorMessages,
      clearErrorMessages,
      searchCustomerName,
      searchBuildingTypeName,
      searchBuildingName,
      searchDeletedStatus,
      getSearchQuery,
      currentPage,
      totalPages,
      handleChangePage,
      searchBuildings,
      searchResetBuildings,
      isSearchExpanded,
      topFixArea,
      tableWrapper,
      paginationContainer
    };
  }
};
</script>

<style scoped>
.list-container {
  padding: 20px 20px 80px 20px;
  margin: auto;
}

.table-wrapper {
  overflow: auto;
  max-height: calc(100vh - 320px);
}

.master-table .btn-cell {
  width: 240px;
  min-width: 240px;
  max-width: 240px;
}

.no-active-building td {
  background-color: #aaa;
}
</style>
